'use client'

import { cn } from '@brew/ui/cn'
import {
  Forward,
  LayoutGrid,
  type LucideIcon,
  Mail,
  Workflow,
} from 'lucide-react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useRef, useState } from 'react'
import { IntentPrefetchLink } from '@/components/navigation/intent-prefetch-link'
import { isEmailCategoryGroup } from '@/lib/email-category/groups'
import { capitalize } from '@/lib/shared/text/capitalize'
import { useTemplateSearchQuery } from '@/lib/templates/search-query-state'
import {
  useFlowsBasePath,
  useTemplatesBasePath,
} from '../lib/templates-surface-context'
import type { CategoryWithCount, TemplateDesignerWithCount } from '../lib/types'
import {
  buildFlowsUrl,
  buildTemplatesUrl,
  FLOWS_TYPE,
} from '../lib/url-helpers'

const FORWARDED_TYPE = 'forwarded'
const SENT_TYPE = 'sent'

const getTypeIcon = (category: string): LucideIcon | null => {
  if (category === 'all') {
    return LayoutGrid
  }
  if (category === FORWARDED_TYPE) {
    return Forward
  }
  if (category === SENT_TYPE) {
    return Mail
  }
  if (category === FLOWS_TYPE) {
    return Workflow
  }
  return null
}

/** `useTranslations('templates')`. */
type TemplatesTranslator = ReturnType<typeof useTranslations<'templates'>>

/**
 * A collection pill and a browse group read in the reader's language; any
 * other category is data (`password-reset`), title-cased.
 */
const formatCategoryLabel = (t: TemplatesTranslator, type: string): string => {
  if (isEmailCategoryGroup(type)) {
    return t(`filters.groups.${type}`)
  }
  if (type === 'all') {
    return t('filters.all')
  }
  if (type === FORWARDED_TYPE) {
    return t('filters.forwarded')
  }
  if (type === SENT_TYPE) {
    return t('filters.sent')
  }
  if (type === FLOWS_TYPE) {
    return t('filters.flows')
  }
  return type
    .split(/\s+/)
    .map((word) => word.split('-').map(capitalize).join('-'))
    .join(' ')
}

interface TemplatesCategoryListProps {
  availableTypes: Array<CategoryWithCount>
  designers?: Array<TemplateDesignerWithCount>
  selectedType?: string
  selectedDesigner?: string
  currentBrand?: string
  showUserTypes?: boolean
}

type FilterItem =
  | { kind: 'category'; value: string; count: number }
  | { kind: 'designer'; value: string; designer: TemplateDesignerWithCount }

function isFilterSelected(
  item: FilterItem,
  selectedType?: string,
  selectedDesigner?: string
): boolean {
  if (item.kind === 'designer') {
    return selectedDesigner === item.value
  }
  if (item.value === 'all') {
    return !(selectedType || selectedDesigner)
  }
  return selectedType === item.value
}

function FilterLeadingIcon({
  item,
  isSelected,
}: {
  item: FilterItem
  isSelected: boolean
}) {
  const t = useTranslations('templates')
  if (item.kind === 'designer') {
    return (
      <span className="mr-1.5 flex shrink-0 items-center border-border border-r pr-1.5">
        <Image
          src={item.designer.logoUrl}
          alt={t('logoAlt', { name: item.designer.name })}
          width={14}
          height={14}
          unoptimized
          className="size-3.5 rounded-full bg-white object-contain"
        />
      </span>
    )
  }

  const TypeIcon = getTypeIcon(item.value)
  if (!TypeIcon) {
    return null
  }

  return (
    <span className="mr-1.5 flex shrink-0 items-center border-border border-r pr-1.5">
      <TypeIcon
        className={cn('size-3', {
          'text-foreground': isSelected,
          'text-foreground/80': !isSelected,
        })}
      />
    </span>
  )
}

export function TemplatesCategoryList({
  availableTypes,
  designers = [],
  selectedType,
  selectedDesigner,
  currentBrand,
  showUserTypes = true,
}: TemplatesCategoryListProps) {
  const t = useTranslations('templates')
  const basePath = useTemplatesBasePath()
  const flowsBasePath = useFlowsBasePath()
  const [searchQuery] = useTemplateSearchQuery()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const allFilters: Array<FilterItem> = [
    { kind: 'category', value: 'all', count: 0 },
    ...(showUserTypes
      ? ([
          { kind: 'category', value: FORWARDED_TYPE, count: 0 },
          { kind: 'category', value: SENT_TYPE, count: 0 },
        ] satisfies Array<FilterItem>)
      : []),
    // The Flows collection is public: everyone sees it, ahead of the
    // designer collections.
    { kind: 'category', value: FLOWS_TYPE, count: 0 },
    ...designers.map(
      (designer): FilterItem => ({
        kind: 'designer',
        value: designer.website,
        designer,
      })
    ),
    ...availableTypes.map(
      (type): FilterItem => ({
        kind: 'category',
        value: type.category,
        count: type.count,
      })
    ),
  ]

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current
    if (!el) {
      return
    }
    setCanScrollLeft(el.scrollLeft > 2)
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) {
      return
    }
    updateScrollState()
    el.addEventListener('scroll', updateScrollState, { passive: true })
    const ro = new ResizeObserver(updateScrollState)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', updateScrollState)
      ro.disconnect()
    }
  }, [updateScrollState])

  const hrefForItem = useCallback(
    (item: FilterItem): string => {
      // Designer pills represent a complete curated collection, so selecting
      // one clears the independent brand facet; a designer collection is not
      // searchable, so it drops the semantic query too.
      if (item.kind === 'designer') {
        return buildTemplatesUrl({ designer: item.value, basePath })
      }
      // The Flows pill leaves the templates gallery for the flows gallery
      // (its own pages, its own index) and keeps the semantic query.
      if (item.value === FLOWS_TYPE) {
        return buildFlowsUrl({ q: searchQuery, basePath: flowsBasePath })
      }
      // Category and Forwarded filters keep the existing brand-combination
      // behavior and persist `q`.
      return buildTemplatesUrl({
        type: item.value === 'all' ? undefined : item.value,
        brand: currentBrand,
        q: searchQuery,
        basePath,
      })
    },
    [currentBrand, basePath, flowsBasePath, searchQuery]
  )

  // The sticky wrapper lives in `TemplatesPageShell` (the brand pill and
  // this strip share one filter row); this component only owns its scroll
  // strip and fade gradients.
  return (
    <div className="relative w-full min-w-0">
      {canScrollLeft ? (
        <div className="pointer-events-none absolute top-0 bottom-0 left-0 z-20 w-8 bg-gradient-to-r from-background to-transparent" />
      ) : null}
      {canScrollRight ? (
        <div className="pointer-events-none absolute top-0 right-0 bottom-0 z-20 w-8 bg-gradient-to-l from-background to-transparent" />
      ) : null}

      <div
        ref={scrollRef}
        className="flex flex-nowrap gap-2 overflow-x-auto py-1 pr-1"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {allFilters.map((item, idx) => {
          const isDesigner = item.kind === 'designer'
          const isAll = item.kind === 'category' && item.value === 'all'
          const isSelected = isFilterSelected(
            item,
            selectedType,
            selectedDesigner
          )
          const count = isDesigner ? item.designer.count : item.count
          const label = isDesigner
            ? item.designer.name
            : formatCategoryLabel(t, item.value)

          return (
            <IntentPrefetchLink
              href={hrefForItem(item)}
              key={`${item.kind}:${item.value}`}
              aria-current={isSelected ? 'page' : undefined}
              className={cn(
                'flex h-8 shrink-0 animate-[fadeInUp_var(--duration-fast)_var(--ease-smooth-out)_both] items-center rounded-lg border border-border px-3 font-medium text-xs transition-colors motion-reduce:animate-none',
                {
                  'border-border bg-white text-foreground dark:border-white/20 dark:bg-white/15':
                    isSelected,
                  'border-border bg-muted/50 text-muted-foreground hover:bg-muted/80 hover:text-foreground/80 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10':
                    !isSelected,
                }
              )}
              // 40ms stagger token, capped so a long strip lands within 300ms.
              style={{
                animationDelay: `calc(var(--duration-stagger) * ${Math.min(idx, 7)})`,
              }}
            >
              <FilterLeadingIcon item={item} isSelected={isSelected} />
              <span className="whitespace-nowrap">{label}</span>
              {!isAll && count > 0 ? (
                <span className="ml-1.5 flex shrink-0 items-center border-border border-l pl-1.5">
                  <span
                    className={cn('text-caption tabular-nums', {
                      'text-foreground': isSelected,
                      'text-muted-foreground': !isSelected,
                    })}
                  >
                    {count}
                  </span>
                </span>
              ) : null}
            </IntentPrefetchLink>
          )
        })}
      </div>
    </div>
  )
}

const SKELETON_WIDTHS = [52, 88, 64, 96, 80, 72, 68, 84]

export function TemplatesCategoryListSkeleton() {
  return (
    <div className="relative w-full min-w-0">
      <div className="flex flex-nowrap gap-1.5 overflow-hidden py-1 pr-1">
        {SKELETON_WIDTHS.map((width, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders — fixed length, never reordered
            key={`cat-skel-${i}`}
            className="h-8 shrink-0 animate-pulse rounded-lg bg-muted/30"
            style={{ width: `${width}px` }}
          />
        ))}
      </div>
    </div>
  )
}
